// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import * as ActivityTypeActions from "../actions"
import * as ActivityTypeSlice from "../slice"
import * as UserContextSlice from "../../user-context/slice"
import * as UserContextActions from "../../user-context/actions"
import {
  ListFavouriteActivityTypesRequest,
  ListFavouriteActivityTypesResponse,
} from "generated/legebill/site/activitytype/v4/activity_type_pb"
import { grpc } from "app/rpc/executor"
import { activityTypeServiceClient } from "app/rpc/client"
import { throttleSame } from "../../../lib/rxutils"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type FetchFavouriteActivityTypesRpc = ({ siteId }: ActivityTypeActions.FetchFavouriteActivityTypesPayload) => Observable<ListFavouriteActivityTypesResponse>

export const fetchFavouriteActivityTypes = (fetchFavouriteActivityTypesRpc: FetchFavouriteActivityTypesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(ActivityTypeActions.fetchFavouriteActivityTypes.match),
      throttleSame(2000, ({ payload: a }, { payload: b }) => a.siteId === b.siteId),
      mergeMap(({ payload }) =>
        of(payload)
          .pipe(
            mergeMap(fetchFavouriteActivityTypesRpc),
            map(t => t.toObject()),
            map(ActivityTypeSlice.setFavouriteActivityTypes),
            catchError(e => handleError(e, payload.swallowFailure)),
          )
      ),
    )

const fetchFavouriteActivityTypesRpc = ({ siteId }: ActivityTypeActions.FetchFavouriteActivityTypesPayload): Observable<ListFavouriteActivityTypesResponse> =>
  grpc(() => {
    const request = new ListFavouriteActivityTypesRequest().setSiteId(siteId)
    return activityTypeServiceClient.listFavouriteActivityTypes(request, null)
  })

const handleError = (error: unknown, swallowFailure: boolean | undefined): Observable<Action> => {
  if (isGrpcError(error)) {
    const { code } = error
    if (code === StatusCode.PERMISSION_DENIED) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  return swallowFailure ?
    EMPTY :
    of(showSnackBar({
      message: t({
        id: "msg_fetch_favourite_activity_types_failed",
        message: "Failed to load favourite activity types. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
      }),
      messageType: "ERROR",
    }))
}

export default fetchFavouriteActivityTypes(fetchFavouriteActivityTypesRpc)
